import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, BoxProps } from 'rebass/styled-components'

import {
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  SmartSelect,
  SmartSelectHeading,
  SmartSelectList,
  SmartSelectItemSingle,
  SmartSelectLoadingContent,
  SmartSelectSearch,
  SmartSelectEmpty,
  SmartSelectGroup
} from '../../Dropdowns'
import { defaultTriggerStyles, defaultContentStyles } from './styles'

export type Option = {
  value: string
  label: string
}
export interface SelectProps extends Omit<BoxProps, 'onChange'> {
  customTriggerStyles?: React.ComponentProps<typeof DropdownTrigger>['sx']
  disabled?: boolean
  groupedOptions?: { heading: string; options: Option[] }[]
  hasHiddenInput?: boolean
  heading?: string
  loading?: boolean
  name?: string
  onChange?: (value: string | undefined) => void
  options?: Option[]
  placeholderId?: string
  searchEmptyMessageId?: string
  searchPlaceholderId?: string
  searchable?: boolean
  selectStyle?: React.ComponentProps<typeof DropdownTrigger>['variant']
  testId?: string
  value: string
}

const Select = ({
  customTriggerStyles,
  disabled,
  groupedOptions,
  hasHiddenInput = true,
  heading,
  loading,
  name,
  onChange,
  options,
  placeholderId,
  searchEmptyMessageId,
  searchPlaceholderId,
  searchable = false,
  selectStyle,
  testId,
  value: defaultValue,
  sx,
  ...props
}: SelectProps): JSX.Element => {
  const { formatMessage } = useIntl()
  const [unifiedOptions, setUnifiedOptions] = useState<Option[]>([])
  const [currentItem, setCurrentItem] = useState<Option | undefined>(undefined)
  useEffect(() => {
    // If groupedOptions are present, flatten into a single array; otherwise simply use options prop
    const unifiedOptions = groupedOptions
      ? groupedOptions.reduce((acc, { options }) => [...acc, ...options], [] as Option[])
      : options || []
    setUnifiedOptions(unifiedOptions)

    // Default value takes precedence, then placeholder, else fallback to first option
    setCurrentItem(
      defaultValue
        ? unifiedOptions.find(({ value }) => value === defaultValue)
        : placeholderId
          ? undefined
          : unifiedOptions[0]
    )

    // Exclude defaultValue from the dependency array to avoid re-runs on value change
  }, [groupedOptions, options, placeholderId])

  const [open, setOpen] = React.useState(false)

  const handleSelect = (selectedLabel: string) => {
    const selectedItem = unifiedOptions.find(({ label }) => label === selectedLabel)
    setCurrentItem(selectedItem)
    onChange && onChange(selectedItem?.value)
    setOpen(false)
  }

  return (
    <Box data-name="Select" sx={{ ...sx }}>
      {hasHiddenInput ? (
        <Box as="input" type="hidden" name={name} value={currentItem?.value || ''} data-testid={testId} {...props} />
      ) : null}
      <Dropdown open={open} onOpenChange={setOpen}>
        <DropdownTrigger
          disabled={disabled}
          label={currentItem?.label || (placeholderId && formatMessage({ id: placeholderId })) || 'Select an option'}
          variant={selectStyle}
          data-testid={testId ? `${testId}-trigger` : undefined}
          sx={{
            ...(customTriggerStyles
              ? {
                  ...customTriggerStyles
                }
              : { ...defaultTriggerStyles })
          }}
        />
        <DropdownContent sx={{ ...defaultContentStyles }} side="bottom">
          <SmartSelect>
            {searchable ? (
              <SmartSelectSearch
                placeholder={(searchPlaceholderId && formatMessage({ id: searchPlaceholderId })) || 'Search'}
              />
            ) : null}
            {heading ? <SmartSelectHeading>{heading}</SmartSelectHeading> : null}
            {/* SmartSelect (cmdk) quirk: when used without a search input, we need a focusable */}
            {/* element within the dropdown for keyboard navigation to work, hence tabIndex below */}
            <SmartSelectList tabIndex={searchable ? undefined : 1}>
              <SmartSelectEmpty>
                {(searchEmptyMessageId && formatMessage({ id: searchEmptyMessageId })) || 'No results'}
              </SmartSelectEmpty>
              {loading ? (
                <SmartSelectLoadingContent />
              ) : (
                <>
                  {groupedOptions?.map(({ heading, options }, index) => (
                    <SmartSelectGroup heading={heading} key={index}>
                      {options.map(({ label, value }) => (
                        <SmartSelectItemSingle
                          key={value}
                          label={label}
                          value={label}
                          active={value === currentItem?.value}
                          onSelect={label => handleSelect(label)}
                        />
                      ))}
                    </SmartSelectGroup>
                  ))}
                  {options?.map(({ label, value }) => (
                    <SmartSelectItemSingle
                      key={value}
                      label={label}
                      value={label}
                      active={value === currentItem?.value}
                      onSelect={label => handleSelect(label)}
                    />
                  ))}
                </>
              )}
            </SmartSelectList>
          </SmartSelect>
        </DropdownContent>
      </Dropdown>
    </Box>
  )
}

export default Select
